import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { epicsApi, type Epic, type EpicStatus } from '../epics/epicsApi';
import { projectsApi, type Project } from '../projects/projectsApi';
import RightPanel from '../components/RightPanel';
import RichTextEditor, { RichTextContent } from '../components/RichTextEditor';
import { DeleteButton } from '../components/DeleteButton';
import { PencilIcon } from '../components/icons';

const EPIC_STATUSES: EpicStatus[] = ['ACTIVE', 'DONE', 'ARCHIVED'];

export default function EpicDetailPage() {
  const { workspaceId = '', epicId = '' } = useParams<{ workspaceId: string; epicId: string }>();
  const { getToken, refresh } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [epic, setEpic] = useState<Epic | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editStatus, setEditStatus] = useState<EpicStatus>('ACTIVE');
  const [saving, setSaving] = useState(false);

  const [projects, setProjects] = useState<Project[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [projectsError, setProjectsError] = useState('');

  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDescription, setNewProjectDescription] = useState('');
  const [creatingProject, setCreatingProject] = useState(false);
  const [showCreateProject, setShowCreateProject] = useState(false);

  const loadEpic = useCallback(() => {
    setLoading(true);
    epicsApi
      .get(workspaceId, epicId, getToken, refresh)
      .then(data => {
        setEpic(data);
        setEditName(data.name);
        setEditDescription(data.description);
        setEditStatus(data.status);
      })
      .catch(() => setError('Failed to load epic'))
      .finally(() => setLoading(false));
  }, [workspaceId, epicId, getToken, refresh]);

  const loadProjects = useCallback(() => {
    setProjectsLoading(true);
    projectsApi
      .list(workspaceId, epicId, getToken, refresh)
      .then(data => setProjects(data.projects))
      .catch(() => setProjectsError('Failed to load projects'))
      .finally(() => setProjectsLoading(false));
  }, [workspaceId, epicId, getToken, refresh]);

  useEffect(() => {
    loadEpic();
  }, [loadEpic]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    if (!editName.trim()) return;
    setSaving(true);
    setError('');
    try {
      const updated = await epicsApi.update(
        workspaceId,
        epicId,
        { name: editName.trim(), description: editDescription.trim(), status: editStatus },
        getToken,
        refresh,
      );
      setEpic(updated);
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update epic');
    } finally {
      setSaving(false);
    }
  }

  function startEdit() {
    if (!epic) return;
    setEditName(epic.name);
    setEditDescription(epic.description);
    setEditStatus(epic.status);
    setError('');
    setEditing(true);
  }

  async function handleDelete() {
    setError('');
    try {
      await epicsApi.remove(workspaceId, epicId, getToken, refresh);
      navigate(`/workspaces/${workspaceId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete epic');
    }
  }

  async function handleCreateProject(e: FormEvent) {
    e.preventDefault();
    if (!newProjectName.trim()) return;
    setCreatingProject(true);
    setProjectsError('');
    try {
      await projectsApi.create(
        workspaceId,
        epicId,
        newProjectName.trim(),
        newProjectDescription.trim(),
        getToken,
        refresh,
      );
      setNewProjectName('');
      setNewProjectDescription('');
      setShowCreateProject(false);
      loadProjects();
    } catch (err) {
      setProjectsError(err instanceof Error ? err.message : 'Failed to create project');
    } finally {
      setCreatingProject(false);
    }
  }

  if (loading) {
    return (
      <main className="page">
        <p className="muted">Loading…</p>
      </main>
    );
  }

  if (!epic) {
    return (
      <main className="page">
        <p className="error">{error || 'Epic not found'}</p>
      </main>
    );
  }

  const panelOpen = editing || showCreateProject;

  return (
    <main className="page" style={{ marginRight: !isMobile && panelOpen ? 456 : undefined }}>
      <p>
        <Link to={`/workspaces/${workspaceId}`}>← Epics</Link>
      </p>

      {error && <p className="error">{error}</p>}

      <div className="card">
        <div className="toolbar--split">
          <h1 style={{ margin: 0 }}>{epic.name}</h1>
          <button type="button" className="icon-btn" title="Edit epic" onClick={startEdit}>
            <PencilIcon />
          </button>
        </div>
        <p className="muted" style={{ margin: '4px 0' }}>{epic.status}</p>
        {epic.description && <RichTextContent html={epic.description} />}
      </div>

      {editing && (
        <RightPanel title="Edit Epic" onClose={() => setEditing(false)}>
          <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <input value={editName} onChange={e => setEditName(e.target.value)} placeholder="Name" required autoFocus />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
              <span>Description</span>
              <RichTextEditor value={editDescription} onChange={setEditDescription} />
            </div>
            <select value={editStatus} onChange={e => setEditStatus(e.target.value as EpicStatus)}>
              {EPIC_STATUSES.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
            <div className="form-actions toolbar--split">
              <div className="toolbar">
                <button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
                <button type="button" onClick={() => setEditing(false)}>Cancel</button>
              </div>
              <DeleteButton onConfirm={handleDelete} title="Delete epic" />
            </div>
          </form>
        </RightPanel>
      )}

      <div className="toolbar" style={{ justifyContent: 'space-between' }}>
        <h2 style={{ margin: 0 }}>Projects</h2>
        <button type="button" onClick={() => setShowCreateProject(true)}>
          + Create project
        </button>
      </div>

      {showCreateProject && (
        <RightPanel title="Create Project" onClose={() => setShowCreateProject(false)}>
          <form onSubmit={handleCreateProject} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {projects.length === 0 && (
              <div style={{ marginBottom: 4 }}>
                <strong>Create your first project</strong>
                <p className="muted" style={{ margin: '4px 0 0', fontSize: 13 }}>
                  Projects are the focused units of work inside an epic. Think of a project as a
                  specific deliverable — a feature, a campaign, or a milestone.
                </p>
              </div>
            )}
            <input
              value={newProjectName}
              onChange={e => setNewProjectName(e.target.value)}
              placeholder="Project name"
              required
              autoFocus
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
              <span>Description</span>
              <RichTextEditor value={newProjectDescription} onChange={setNewProjectDescription} />
            </div>
            <div className="form-actions">
              <button type="submit" disabled={creatingProject}>
                {creatingProject ? 'Creating…' : 'Create project'}
              </button>
              <button type="button" onClick={() => setShowCreateProject(false)}>Cancel</button>
            </div>
          </form>
        </RightPanel>
      )}

      {projectsLoading && <p className="muted">Loading projects…</p>}
      {projectsError && <p className="error">{projectsError}</p>}
      {!projectsLoading && projects.length === 0 && <p className="muted">No projects yet.</p>}

      <ul className="card-list">
        {projects.map(project => (
          <li key={project.projectId} className="card card-clickable">
            <Link to={`/workspaces/${workspaceId}/projects/${project.projectId}`} className="card-link-block">
              <strong className="card-link">{project.name}</strong>
              <p className="muted" style={{ margin: '4px 0 0', fontSize: 12 }}>{project.status}</p>
              {project.description && <RichTextContent html={project.description} />}
            </Link>
          </li>
        ))}
      </ul>
    </main>
  );
}
